"use client"

import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ExternalLink } from "lucide-react"

export interface ComboConfiguration {
  targetUrl: string
  anchorText: string
  location: string
  linksNeeded: number
  linksFulfilled: number
}

interface ComboConfigurationCellProps {
  combos: ComboConfiguration[]
}

export function ComboConfigurationCell({ combos }: ComboConfigurationCellProps) {
  if (!combos || combos.length === 0) {
    return <span className="text-neutral-400">—</span>
  }

  const firstCombo = combos[0]
  const hasMultiple = combos.length > 1

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" className="h-auto p-2 hover:bg-neutral-100 text-left justify-start w-full">
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs text-neutral-500 truncate max-w-[200px]" title={firstCombo.targetUrl}>
                {firstCombo.targetUrl}
              </span>
              <ExternalLink className="w-3 h-3 text-neutral-400 flex-shrink-0" />
            </div>
            <span className="text-xs text-neutral-700 font-medium truncate max-w-[200px]" title={firstCombo.anchorText}>
              {firstCombo.anchorText}
            </span>
            {hasMultiple && (
              <span className="text-xs text-blue-600 font-medium">
                +{combos.length - 1} more combo{combos.length - 1 > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[500px] p-0" align="start">
        <div className="bg-neutral-50 px-4 py-3 border-b border-neutral-200">
          <h3 className="font-semibold text-neutral-900">Order Combo Configurations</h3>
          <p className="text-xs text-neutral-600 mt-1">
            {combos.length} combination{combos.length > 1 ? "s" : ""} total
          </p>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {combos.map((combo, index) => (
            <div key={index} className="px-4 py-3 border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-neutral-500 mb-1">Target URL</div>
                  <a
                    href={combo.targetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline break-all flex items-center gap-1"
                  >
                    {combo.targetUrl}
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  </a>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div>
                  <div className="text-xs text-neutral-500 mb-1">Anchor Text</div>
                  <div className="text-sm font-medium text-neutral-900">{combo.anchorText}</div>
                </div>
                <div>
                  <div className="text-xs text-neutral-500 mb-1">Location</div>
                  <div className="text-sm text-neutral-900">{combo.location}</div>
                </div>
                <div>
                  <div className="text-xs text-neutral-500 mb-1">Links Fulfilled</div>
                  <div
                    className={`text-sm font-semibold ${combo.linksFulfilled < combo.linksNeeded ? "text-red-600" : "text-neutral-900"}`}
                  >
                    {combo.linksFulfilled} of {combo.linksNeeded}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
